import type React from 'react';

/**
 * Loaded ESM module for a custom component. The artifact's default export is the React
 * component; other named exports are forwarded as-is in case the author re-exports utilities.
 */
export interface LoadedCustomComponentModule {
  default: unknown;
  [key: string]: unknown;
}

/**
 * Runtime handle the host app (editor canvas or live published page) hands to dynamically-loaded
 * custom components. The compiled artifact reads this directly off `globalThis.<MUTINY_HOST_RUNTIME_KEY>`
 * — no shim modules, no client-side rewriting.
 *
 *   - `React` is the host's singleton. Reusing it avoids dual-React hooks errors when the artifact
 *     mounts as a React subtree.
 *   - `loadComponent` resolves a sibling component (`import X from 'components/<slug>'` in the
 *     author's source) against the page's current manifest, returning the awaited module.
 */
export interface MutinyHostRuntime {
  React: typeof React;
  loadComponent: (slug: string) => Promise<LoadedCustomComponentModule>;
}

export const MUTINY_HOST_RUNTIME_KEY = '__mutinyHostRuntime';

interface GlobalWithRuntime {
  [MUTINY_HOST_RUNTIME_KEY]?: MutinyHostRuntime;
}

export function registerHostRuntime(runtime: MutinyHostRuntime): void {
  (globalThis as GlobalWithRuntime)[MUTINY_HOST_RUNTIME_KEY] = runtime;
}
